import React from "react"

import { AlertCircle } from "lucide-react"

import { ComplianceFiltersPopover } from "@/components/compliance/filters/ComplianceFiltersPopover"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Badge } from "@/components/ui/badge"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Skeleton } from "@/components/ui/skeleton"
import { useComplianceFilters } from "@/contexts/ComplianceFiltersContext"
import { ComplianceOverviewMetricKey, useComplianceOverview } from "@/hooks/useComplianceOverview"

const statusColor = (percentage: number) => {
  if (percentage >= 90) return "bg-green-100 text-green-800"
  if (percentage >= 50) return "bg-yellow-100 text-yellow-800"
  return "bg-red-100 text-red-800"
}

const sections: { title: string; keys: Set<ComplianceOverviewMetricKey> }[] = [
  {
    title: "Engagement",
    keys: new Set(["vatNumber", "utrNumber", "paymentInfo", "licencesChecked"]),
  },
  {
    title: "Documents",
    keys: new Set(["rightToWork", "licenceFrontBack", "bankStatement", "serviceLevelAgreement"]),
  },
]

export const ComplianceOverview: React.FC = () => {
  const {
    locationFilter,
    sheriffTypeFilter,
    driverManagerTeamFilter,
    dspFilter,
    eligibilityFilter,
  } = useComplianceFilters()

  const { data, isLoading, error } = useComplianceOverview({
    searchQuery: "",
    locationFilter: locationFilter !== "all" ? locationFilter : "",
    sheriffTypeFilter: sheriffTypeFilter !== "all" ? sheriffTypeFilter : "",
    driverManagerTeamFilter: driverManagerTeamFilter !== "all" ? driverManagerTeamFilter : "",
    dspFilter: dspFilter !== "all" ? dspFilter : "",
    eligibilityFilter,
  })

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {Array.from({ length: 9 }).map((_, idx) => (
          <Skeleton key={idx} className="h-28 w-full" />
        ))}
      </div>
    )
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          Failed to load compliance overview:{" "}
          {error instanceof Error ? error.message : "An unknown error occurred"}
        </AlertDescription>
      </Alert>
    )
  }

  const metrics = data?.metrics || []

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Compliance Center</h2>
          <p className="text-sm text-palette-jungleGreen600">
            Showing compliance for active Rc1 Deliveries, Rc2 Deliveries, and Collections drivers
            only
          </p>
        </div>
        <ComplianceFiltersPopover
          title="Filter Compliance Overview"
          description="Refine counts with the same filters as licence checks"
        />
      </div>

      {sections.map((section) => {
        const sectionMetrics = metrics.filter((m) => section.keys.has(m.key))
        if (sectionMetrics.length === 0) return null

        return (
          <div key={section.title} className="space-y-2">
            <div>
              <h3 className="text-lg font-semibold">{section.title}</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {sectionMetrics.map((metric) => (
                <Card key={metric.key} className="border-2">
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <CardTitle className="text-sm text-palette-jungleGreen600">
                          {metric.title}
                        </CardTitle>
                        <p className="text-xs text-palette-jungleGreen500">{metric.description}</p>
                      </div>
                      <Badge className={statusColor(metric.percentage)}>
                        {metric.percentage.toFixed(1)}%
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="text-2xl font-bold">
                      {metric.compliant} / {metric.total}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )
      })}
    </div>
  )
}

export default ComplianceOverview
